import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, Skeleton, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, RobotOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import StatCard from '@/components/StatCard';
import RiskTag from '@/components/RiskTag';
import Panel from '@/components/Panel';
import ProgressBar from '@/components/ProgressBar';
import { DATA_NOT_READY_HINT, isWeekDataReady, type WeekStatus } from '@/lib/weekData';
import { api } from '@/lib/api';
import { useBreakpoint } from '@/lib/breakpoints';
import { useWeekStore } from '@/stores/weekStore';
import {
  RISK_DIMENSIONS,
  RECALL_YTD_LABEL,
  RECALL_YTD_TOOLTIP,
  COMPLAINT_7D_HINT,
  sortByRiskDimension,
  type RiskDimension,
} from './overviewRisk';

type RiskLevel = '低' | '中' | '高' | '重大';

interface BrandRiskItem {
  brand_id: string;
  brand_name: string;
  risk_score: number;
  risk_level: RiskLevel;
  complaint_count_7d: number;
  recall_count_ytd: number;
  sentiment_score: number;
}

interface RecallItem {
  brand_model: string;
  defect: string;
  vehicle_count: number;
  announcement_date: string;
  risk_level: RiskLevel;
}

interface GrowthItem {
  rank: number;
  brand: string;
  model: string;
  this_week: number;
  last_week: number;
  wow: number;
  issues: string[];
  risk_level: RiskLevel;
}

interface KeywordItem {
  word: string;
  weight: number;
}

type DataSourceDisplayStatus =
  | '成功'
  | '部分失败'
  | '无新增'
  | '已停用'
  | '重构中'
  | '未运行'
  | '失败';

interface DataSourceStatusItem {
  source_name: string;
  status: DataSourceDisplayStatus;
  items: number;
  quality: number; // 0-100
  duration_min?: number;
  note?: string;
  latest_run_status?: string | null;
  latest_run_at?: string | null;
}

interface ComplaintTrend {
  weeks: string[];
  series: Array<{
    brand: string;
    color: string;
    data: number[];
  }>;
}

interface IssueDistributionItem {
  name: string;
  value: number;
  color: string;
}

interface OverviewData {
  week: string;
  kpi: {
    platform_score_median: number;
    platform_score_change: number;
    complaints_total: number;
    complaints_wow: number;
    recalls_new: number;
    recalls_vehicle_count: number;
    negative_sentiment_ratio: number;
    high_risk_models: number;
  };
  brand_risk: BrandRiskItem[];
  weekly_recalls: RecallItem[];
  top_models_growth: GrowthItem[];
  negative_keywords: KeywordItem[];
  data_sources: DataSourceStatusItem[];
  complaint_trend: ComplaintTrend;
  issue_distribution: IssueDistributionItem[];
  // PR 9.2g 三态: ready = recompute 完成有 metrics; data_not_ready = 周已结束但 metrics 未生成
  week_status?: WeekStatus;
}

// =====================================================
// Page
// =====================================================
export default function OverviewPage() {
  const { week } = useWeekStore();
  // 品牌风险排行：当前筛选维度（默认综合风险）。re-sort 纯客户端，0 后端请求。
  const [activeRiskDim, setActiveRiskDim] = useState<RiskDimension>('综合风险');

  const { data: apiData, isLoading } = useQuery({
    queryKey: ['overview', week],
    queryFn: async () => {
      const res = await api.get<OverviewData>('/overview', { params: { week } });
      return res.data;
    },
    retry: false, // Phase 1 阶段 /overview 还没实现，不重试
  });

  // API 不可用时降级到 mock（Phase 1 骨架阶段允许；Phase 3 会落真接口）
  const data = apiData ?? MOCK_OVERVIEW;
  const usingMock = !apiData;

  if (isLoading && !apiData) return <Skeleton active paragraph={{ rows: 12 }} />;
  if (!data) return <Empty description="加载失败" />;

  const {
    kpi,
    brand_risk,
    weekly_recalls,
    top_models_growth,
    negative_keywords,
    complaint_trend,
    issue_distribution,
  } = data;
  // PR 9.2k: 本周数据是否就绪(undefined 视为 ready,向后兼容旧 API)。
  const weekReady = isWeekDataReady(data.week_status);
  // 品牌风险排行按所选维度客户端 re-sort（仅重排现有品牌，0 增删/0 后端）。
  const sortedBrandRisk = sortByRiskDimension(brand_risk, activeRiskDim);

  return (
    <div className="space-y-5">
      {/* Mock data banner — PR 9.2a: 真实 API 已上线, 仅在 API 请求失败时显示 */}
      {usingMock && (
        <div
          className="rounded-lg border px-4 py-2.5 text-[13px] flex items-center gap-2"
          style={{ background: '#FFFBEB', borderColor: '#FCD34D', color: '#92400E' }}
        >
          <span>ℹ️</span>
          <span>
            <b>Overview API 请求失败，当前显示模拟数据</b>
          </span>
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">
            首页总览 · {week}
          </h1>
          {/* PR 9.2m microfix v2: not-ready 周不显硬编码采集总量(W23 红线) */}
          <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
            {weekReady
              ? '本周自动抓取覆盖全部已配置数据源、品牌与车型。'
              : '本周数据采集中，预计周一 03:10 CST recompute 后展示完整采集口径。'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button icon={<DownloadOutlined />}>导出周报 Excel</Button>
          <Button type="primary" icon={<RobotOutlined />}>
            查看 Agent 摘要
          </Button>
        </div>
      </div>

      {/* KPI row — PR 9.2m: not-ready 周整组 gate(W23 红线,不显硬 0/-100%/编造环比) */}
      {!weekReady ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-[13px] text-slate-500">
          {DATA_NOT_READY_HINT}
        </div>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        <StatCard
          title="平台综合口碑分 中位数"
          value={kpi.platform_score_median.toFixed(1)}
          trend={`↓ ${Math.abs(kpi.platform_score_change).toFixed(1)}`}
          trendType="down"
          hint="公开平台口碑评分(1-5)，非问卷净推荐值"
        />
        <StatCard
          title="本周新增投诉"
          value={kpi.complaints_total.toLocaleString()}
          trend={`↑ ${kpi.complaints_wow.toFixed(1)}%`}
          trendType="up"
        />
        <StatCard
          title="本周新增召回"
          value={kpi.recalls_new}
          hint={`涉及 ${kpi.recalls_vehicle_count.toLocaleString()} 辆`}
        />
        <StatCard
          title="负面情绪占比"
          value={`${(kpi.negative_sentiment_ratio * 100).toFixed(1)}%`}
          trend="↑ 2.1pp"
          trendType="up"
          hint="连续 2 周上升"
        />
        <StatCard
          title="高风险车型数"
          value={kpi.high_risk_models}
          trend="↑ 1"
          trendType="up"
        />
      </div>
      )}

      {/* Row 1: Brand risk (2/3) + Weekly recalls (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel
            title="品牌风险排行（综合风险分）"
            extra={
              <div className="flex gap-1.5">
                {RISK_DIMENSIONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setActiveRiskDim(label)}
                    className={`px-3 py-1 text-[12px] rounded-full border transition-colors
                      ${
                        label === activeRiskDim
                          ? 'bg-gac-primary text-white border-gac-primary'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          >
            {!weekReady ? (
              <div className="py-8 flex items-center justify-center">
                <Empty description={DATA_NOT_READY_HINT} />
              </div>
            ) : (
            <div className="space-y-4">
              {sortedBrandRisk.map((b) => (
                <div key={b.brand_id}>
                  <div className="flex justify-between text-[13px] mb-1.5">
                    <span className="font-medium flex items-center gap-2">
                      {b.brand_name}
                      <RiskTag level={b.risk_level} score={b.risk_score} />
                    </span>
                    <span className="text-slate-500">
                      <span title={`投诉数=${COMPLAINT_7D_HINT}新增（complaint_count_7d）`}>
                        投诉（{COMPLAINT_7D_HINT}） {b.complaint_count_7d}
                      </span>{' '}
                      · <span title={RECALL_YTD_TOOLTIP}>{RECALL_YTD_LABEL} {b.recall_count_ytd}</span>{' '}
                      · 情绪 {b.sentiment_score.toFixed(2)}
                    </span>
                  </div>
                  <ProgressBar
                    value={b.risk_score}
                    color={
                      b.risk_level === '重大'
                        ? '#DC2626'
                        : b.risk_level === '高'
                          ? '#EA580C'
                          : b.risk_level === '中'
                            ? '#F59E0B'
                            : '#16A34A'
                    }
                  />
                </div>
              ))}
            </div>
            )}
          </Panel>
        </div>

        <Panel
          title="本周召回事件"
          extra={
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[11.5px] font-medium bg-red-100 text-red-700">
                {weekly_recalls.length} 起
              </span>
              <Link to="/recalls" className="text-[12px] text-blue-600 hover:underline">
                查看全部 →
              </Link>
            </div>
          }
        >
          {!weekReady ? (
            <div className="py-8 flex items-center justify-center">
              <Empty description={DATA_NOT_READY_HINT} />
            </div>
          ) : (
          <div className="space-y-3">
            {weekly_recalls.map((r, i) => (
              <div
                key={i}
                className="border-l-2 pl-3"
                style={{ borderColor: r.risk_level === '重大' ? '#DC2626' : '#F59E0B' }}
              >
                <div className="text-[13px] font-medium text-slate-800">{r.brand_model}</div>
                <div className="text-[12px] text-slate-500 mt-1">{r.defect}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Tag color={r.risk_level === '重大' ? 'red' : 'orange'}>
                    {r.vehicle_count.toLocaleString()} 辆
                  </Tag>
                  <span className="text-[11px] text-slate-400">{r.announcement_date}</span>
                </div>
              </div>
            ))}
          </div>
          )}
        </Panel>
      </div>

      {/* Row 1.5: Complaint trend (2/3) + Issue distribution (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel
            title="近 12 周投诉量趋势（按品牌）"
            extra={<span className="text-[12px] text-slate-500">单位：条</span>}
          >
            {!weekReady || complaint_trend.weeks.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center">
                <Empty description={DATA_NOT_READY_HINT} />
              </div>
            ) : (
              <ComplaintTrendChart data={complaint_trend} />
            )}
          </Panel>
        </div>
        <Panel title="本周投诉一级分类">
          {!weekReady ? (
            <div className="h-[280px] flex items-center justify-center">
              <Empty description={DATA_NOT_READY_HINT} />
            </div>
          ) : (() => {
            // PR 9.2a D 方案: 检测 issue_distribution 全部 fallback gray (#94A3B8) → 改渲 AntD Empty.
            // root cause: DB issue_category_l1 写入用 v2 8 桶 classifier, overview.py
            // _CATEGORY_COLORS 仍是 IssueCategoryL1 9 桶 canonical, lookup 100% miss → 全 fallback.
            // schema 治理留 9.2b, 本 PR 仅前端兜底, 不动 API/classifier.
            const allGray =
              issue_distribution.length > 0 &&
              issue_distribution.every(
                (d) => String(d.color).trim().toUpperCase() === '#94A3B8',
              );
            return allGray ? (
              <div className="h-[280px] flex items-center justify-center">
                <Empty description="投诉分类口径正在升级中，分布图暂不展示" />
              </div>
            ) : (
              <IssueDistributionChart data={issue_distribution} />
            );
          })()}
        </Panel>
      </div>

      {/* Row 2: Top models growth (2/3) + Negative keywords (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel
            title="车型投诉增长榜 Top 8（周环比）"
            extra={
              <Link to="/complaints" className="text-[12px] text-blue-600 hover:underline">
                投诉矩阵 →
              </Link>
            }
          >
            {!weekReady ? (
              <div className="py-8 flex items-center justify-center">
                <Empty description={DATA_NOT_READY_HINT} />
              </div>
            ) : (
              <Table
                rowKey="rank"
                columns={growthColumns}
                dataSource={top_models_growth.slice(0, 8)}
                pagination={false}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 720 }}
              />
            )}
          </Panel>
        </div>

        <Panel title="本周负面高频词">
          {!weekReady ? (
            <div className="py-8 flex items-center justify-center">
              <Empty description={DATA_NOT_READY_HINT} />
            </div>
          ) : (
          <div className="text-center py-2">
            {negative_keywords.map((kw, i) => (
              <span
                key={kw.word}
                className="inline-block px-2 py-0.5 m-1"
                style={{
                  fontSize: `${10 + kw.weight * 0.6}px`,
                  color: `hsl(${360 - i * 10}, 75%, ${30 + i * 2}%)`,
                  fontWeight: kw.weight >= 20 ? 600 : 400,
                }}
              >
                {kw.word}
              </span>
            ))}
          </div>
          )}
        </Panel>
      </div>

    </div>
  );
}

// =====================================================
// Growth table columns
// =====================================================
const growthColumns: ColumnsType<GrowthItem> = [
  {
    title: '排名',
    dataIndex: 'rank',
    width: 60,
    render: (r: number) => (
      <Tag color={r <= 2 ? 'red' : r <= 4 ? 'orange' : 'default'}>{r}</Tag>
    ),
  },
  {
    title: '品牌 / 车型',
    key: 'bm',
    width: 160,
    render: (_, row) => (
      <span className="font-medium">
        {row.brand} · {row.model}
      </span>
    ),
  },
  { title: '本周', dataIndex: 'this_week', width: 60 },
  { title: '上周', dataIndex: 'last_week', width: 60 },
  {
    title: '环比',
    dataIndex: 'wow',
    width: 90,
    render: (w: number) => <span style={{ color: '#DC2626' }}>↑ {w.toFixed(1)}%</span>,
  },
  {
    // 9.2j(J2): 固定宽 + 截断,防横向溢出
    title: '主要问题',
    dataIndex: 'issues',
    width: 200,
    ellipsis: true,
    render: (issues: string[]) =>
      issues.map((i) => (
        <Tag key={i} style={{ marginInlineEnd: 4 }}>
          {i}
        </Tag>
      )),
  },
  {
    title: '风险',
    dataIndex: 'risk_level',
    width: 90,
    render: (l: RiskLevel) => <RiskTag level={l} />,
  },
];

// =====================================================
// Local mock — replaced by real /api/v2/overview in Phase 3
// =====================================================
const MOCK_OVERVIEW: OverviewData = {
  week: '2026-W19',
  kpi: {
    platform_score_median: 42.8,
    platform_score_change: 1.3,
    complaints_total: 1236,
    complaints_wow: 18.4,
    recalls_new: 2,
    recalls_vehicle_count: 84396,
    negative_sentiment_ratio: 0.316,
    high_risk_models: 4,
  },
  brand_risk: [
    {
      brand_id: 'GAC-AION',
      brand_name: '广汽埃安',
      risk_score: 72,
      risk_level: '高',
      complaint_count_7d: 487,
      recall_count_ytd: 0,
      sentiment_score: -0.42,
    },
    {
      brand_id: 'GAC-TRUMP',
      brand_name: '广汽传祺',
      risk_score: 58,
      risk_level: '中',
      complaint_count_7d: 312,
      recall_count_ytd: 1,
      sentiment_score: -0.18,
    },
    {
      brand_id: 'GAC-HONDA',
      brand_name: '广汽本田',
      risk_score: 54,
      risk_level: '中',
      complaint_count_7d: 287,
      recall_count_ytd: 1,
      sentiment_score: -0.21,
    },
    {
      brand_id: 'GAC-HYPER',
      brand_name: '昊铂',
      risk_score: 41,
      risk_level: '中',
      complaint_count_7d: 38,
      recall_count_ytd: 0,
      sentiment_score: -0.09,
    },
    {
      brand_id: 'GAC-TOYOTA',
      brand_name: '广汽丰田',
      risk_score: 28,
      risk_level: '低',
      complaint_count_7d: 112,
      recall_count_ytd: 0,
      sentiment_score: 0.12,
    },
  ],
  weekly_recalls: [
    {
      brand_model: '广汽本田 P7 / 雅阁 e:PHEV',
      defect: '动力控制软件缺陷 · OTA 修复',
      vehicle_count: 18274,
      announcement_date: '2025-12-31',
      risk_level: '重大',
    },
    {
      brand_model: '广汽传祺 GS8 (2022款)',
      defect: '组合仪表显示异常 · OTA 修复',
      vehicle_count: 74422,
      announcement_date: '2025-01-03',
      risk_level: '高',
    },
  ],
  top_models_growth: [
    {
      rank: 1,
      brand: '广汽埃安',
      model: 'AION S',
      this_week: 187,
      last_week: 92,
      wow: 103.3,
      issues: ['续航虚标', '系统故障'],
      risk_level: '重大',
    },
    {
      rank: 2,
      brand: '广汽本田',
      model: 'P7',
      this_week: 64,
      last_week: 21,
      wow: 204.8,
      issues: ['动力中断', '仪表故障'],
      risk_level: '重大',
    },
    {
      rank: 3,
      brand: '广汽传祺',
      model: 'GS8',
      this_week: 89,
      last_week: 52,
      wow: 71.2,
      issues: ['车机黑屏', '变速箱'],
      risk_level: '高',
    },
    {
      rank: 4,
      brand: '广汽埃安',
      model: 'AION Y',
      this_week: 118,
      last_week: 78,
      wow: 51.3,
      issues: ['底盘异响', '续航'],
      risk_level: '高',
    },
    {
      rank: 5,
      brand: '广汽本田',
      model: '雅阁',
      this_week: 92,
      last_week: 71,
      wow: 29.6,
      issues: ['变速箱', 'CVT 顿挫'],
      risk_level: '中',
    },
    {
      rank: 6,
      brand: '广汽传祺',
      model: 'M8',
      this_week: 61,
      last_week: 49,
      wow: 24.5,
      issues: ['车机', '异响'],
      risk_level: '中',
    },
    {
      rank: 7,
      brand: '昊铂',
      model: 'HT',
      this_week: 22,
      last_week: 18,
      wow: 22.2,
      issues: ['智驾', 'OTA'],
      risk_level: '中',
    },
    {
      rank: 8,
      brand: '广汽丰田',
      model: '凯美瑞',
      this_week: 48,
      last_week: 41,
      wow: 17.1,
      issues: ['机油', '异响'],
      risk_level: '低',
    },
  ],
  negative_keywords: [
    { word: '续航虚标', weight: 24 },
    { word: '动力中断', weight: 22 },
    { word: '车机黑屏', weight: 20 },
    { word: '锁电', weight: 19 },
    { word: '底盘异响', weight: 17 },
    { word: 'CVT 顿挫', weight: 16 },
    { word: '充电慢', weight: 15 },
    { word: '系统故障', weight: 15 },
    { word: '续航不准', weight: 14 },
    { word: '仪表异常', weight: 14 },
    { word: '风噪', weight: 13 },
    { word: 'OTA 失败', weight: 13 },
    { word: '售后慢', weight: 12 },
    { word: '配件等', weight: 12 },
    { word: '座椅硬', weight: 12 },
  ],
  data_sources: [
    { source_name: '懂车帝', status: '成功', items: 3247, duration_min: 18, quality: 98 },
    { source_name: '易车', status: '成功', items: 2156, duration_min: 12, quality: 95 },
    {
      source_name: '汽车之家',
      status: '部分失败',
      items: 1892,
      duration_min: 0,
      quality: 82,
      note: '3 条 parser 异常',
    },
    { source_name: '车质网', status: '成功', items: 1236, duration_min: 8, quality: 96 },
    {
      source_name: '召回中心',
      status: '成功',
      items: 2,
      duration_min: 0,
      quality: 100,
      note: '新增 2 条召回',
    },
  ],
  complaint_trend: {
    weeks: ['W8', 'W9', 'W10', 'W11', 'W12', 'W13', 'W14', 'W15', 'W16', 'W17', 'W18', 'W19'],
    series: [
      {
        brand: '广汽埃安',
        color: '#7C3AED',
        data: [210, 232, 221, 256, 247, 289, 312, 341, 358, 378, 358, 487],
      },
      {
        brand: '广汽传祺',
        color: '#2563EB',
        data: [267, 254, 278, 289, 275, 281, 295, 288, 302, 294, 287, 312],
      },
      {
        brand: '广汽本田',
        color: '#DC2626',
        data: [198, 212, 205, 221, 216, 229, 237, 242, 251, 263, 247, 287],
      },
      {
        brand: '广汽丰田',
        color: '#059669',
        data: [156, 148, 142, 135, 128, 131, 122, 118, 109, 115, 108, 112],
      },
      {
        brand: '昊铂',
        color: '#F59E0B',
        data: [12, 18, 22, 28, 29, 31, 33, 35, 32, 36, 34, 38],
      },
    ],
  },
  // PR 9.2b-1: 拆 电池/三电 → 三电系统 + 续航与充电 (C2 10 桶);
  // labels ⊆ IssueCategoryL1, set equality 由 apps/web/src/lib/__tests__/l1_taxonomy.test.ts 校验.
  // canonical color 跟 apps/api/src/routers/overview.py _CATEGORY_COLORS 对齐 (priority: API live data wins).
  issue_distribution: [
    { name: '智能座舱', value: 158, color: '#8B5CF6' },
    { name: '三电系统', value: 88, color: '#10B981' },
    { name: '续航与充电', value: 53, color: '#14B8A6' },
    { name: '动力系统', value: 139, color: '#3B82F6' },
    { name: '车身/底盘', value: 102, color: '#06B6D4' },
    { name: '售后服务', value: 62, color: '#EC4899' },
    { name: '制动/转向', value: 34, color: '#EF4444' },
    { name: '智驾/辅助驾驶', value: 21, color: '#F59E0B' },
    { name: '内饰/配置', value: 79, color: '#84CC16' },
    { name: '销售争议', value: 41, color: '#6B7280' },
  ],
};

// =====================================================
// 近 12 周投诉量趋势 · 折线图
// =====================================================
function ComplaintTrendChart({ data }: { data: ComplaintTrend }) {
  const { isMobile } = useBreakpoint();
  const option: EChartsOption = {
    grid: {
      left: isMobile ? 36 : 50,
      right: isMobile ? 12 : 20,
      top: 16,
      bottom: isMobile ? 70 : 60,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'cross', label: { backgroundColor: '#475569' } },
    },
    legend: {
      bottom: 0,
      icon: 'roundRect',
      type: isMobile ? 'scroll' : 'plain',
      itemWidth: 14,
      itemHeight: 8,
      textStyle: { fontSize: isMobile ? 10 : 12, color: '#475569' },
    },
    xAxis: {
      type: 'category',
      data: data.weeks,
      axisLine: { lineStyle: { color: '#E2E8F0' } },
      axisLabel: { color: '#64748B', fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 0 },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#64748B', fontSize: isMobile ? 9 : 11 },
      splitLine: { lineStyle: { color: '#F1F5F9', type: 'dashed' } },
    },
    series: data.series.map((s) => ({
      name: s.brand,
      type: 'line',
      data: s.data,
      smooth: true,
      symbol: 'circle',
      symbolSize: isMobile ? 4 : 6,
      itemStyle: { color: s.color },
      lineStyle: { color: s.color, width: 2 },
      emphasis: { focus: 'series' },
    })),
  };

  return <ReactECharts option={option} style={{ height: isMobile ? 240 : 280 }} notMerge lazyUpdate />;
}

// =====================================================
// 本周投诉一级分类 · 环形饼图
// =====================================================
function IssueDistributionChart({ data }: { data: IssueDistributionItem[] }) {
  const { isMobile } = useBreakpoint();
  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}: {c} ({d}%)',
    },
    legend: isMobile
      ? {
          orient: 'horizontal',
          bottom: 0,
          type: 'scroll',
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 10, color: '#475569' },
        }
      : {
          orient: 'vertical',
          right: 8,
          top: 'center',
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: '#475569' },
        },
    series: [
      {
        type: 'pie',
        radius: ['52%', '78%'],
        center: isMobile ? ['50%', '42%'] : ['35%', '50%'],
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        data: data.map((d) => ({
          value: d.value,
          name: d.name,
          itemStyle: { color: d.color },
        })),
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: isMobile ? 260 : 280 }} notMerge lazyUpdate />;
}
